import { useState } from "react";
import { Globe, Image as ImageIcon, MoreHorizontal, PlayCircle, Layers, ThumbsUp, MessageCircle, Share2 } from "lucide-react";
import { API } from "../../api";

// What the ad will actually look like once it runs. Rendered from the campaign's own creative
// fields, so the preview can never drift from what gets pushed.
//
// The media area is deliberately a LABELLED PLACEHOLDER, not a stock photo: until the gym
// uploads its own image/video there is no real creative, and dropping in an attractive stock
// image would make the preview a promise the published ad wouldn't keep.

const FORMAT_ICON = { video: PlayCircle, carousel: Layers, slideshow: Layers, image: ImageIcon, text: ImageIcon };

function initials(name = "") {
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "G";
}

function hostFrom(url, fallback) {
  if (!url) return fallback;
  try {
    return new URL(url.startsWith("http") ? url : `https://${url}`).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function CreativeMedia({ creative, height = 200 }) {
  // Once the gym attaches its own file we show the real thing; until then a labelled
  // placeholder, never a stock photo standing in for a creative that doesn't exist.
  if (creative.asset_key) {
    // Absolute, because the API is served from its own origin in dev. The session cookie still
    // rides along: different ports are the same *site*, which is what SameSite actually gates on.
    const src = `${API}/marketing/creatives/${creative.asset_key}`;
    if (creative.format === "video") {
      return (
        <video
          src={src} controls muted playsInline
          style={{ width: "100%", height, objectFit: "cover", background: "#000", display: "block" }}
        />
      );
    }
    return (
      <img
        src={src} alt={creative.headline || "Ad creative"}
        style={{ width: "100%", height, objectFit: "cover", display: "block" }}
      />
    );
  }
  return <MediaPlaceholder creative={creative} height={height} />;
}

function MediaPlaceholder({ creative, height = 200 }) {
  const Icon = FORMAT_ICON[creative.format] || ImageIcon;
  return (
    <div
      className="flex flex-col items-center justify-center gap-1.5 text-center px-4"
      style={{ height, background: "linear-gradient(135deg,#F3F4F6,#E5E7EB)", borderTop: "1px solid #E5E7EB", borderBottom: "1px solid #E5E7EB" }}
    >
      <Icon size={26} style={{ color: "#9CA3AF" }} strokeWidth={1.5} />
      <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: "#6B7280" }}>{creative.format}</p>
      {creative.content && <p className="text-[11px]" style={{ color: "#9CA3AF" }}>{creative.content}</p>}
      <p className="text-[10px]" style={{ color: "#B0B5BD" }}>Your image or video appears here</p>
    </div>
  );
}

// Facebook / Instagram feed placement.
function MetaPreview({ creative, gymName, displayUrl }) {
  return (
    <div className="rounded-[10px] overflow-hidden" style={{ background: "#FFFFFF", border: "1px solid #DDE1E7", maxWidth: 380 }}>
      <div className="flex items-center gap-2 p-3">
        <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ background: "#D6202B" }}>
          {initials(gymName)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold leading-tight truncate" style={{ color: "#050505" }}>{gymName}</p>
          <p className="text-[11px] flex items-center gap-1" style={{ color: "#65676B" }}>
            Sponsored · <Globe size={10} />
          </p>
        </div>
        <MoreHorizontal size={16} style={{ color: "#65676B" }} />
      </div>

      <p className="px-3 pb-2 text-[13px] leading-snug" style={{ color: "#050505" }}>{creative.body}</p>

      <CreativeMedia creative={creative} />

      <div className="flex items-center gap-2 px-3 py-2.5" style={{ background: "#F7F8FA" }}>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase truncate" style={{ color: "#65676B" }}>{displayUrl}</p>
          <p className="text-[13px] font-semibold leading-tight truncate" style={{ color: "#050505" }}>{creative.headline}</p>
        </div>
        <button className="text-[12px] font-semibold px-3 py-1.5 rounded-[6px] shrink-0" style={{ background: "#E4E6EB", color: "#050505" }}>
          {creative.cta}
        </button>
      </div>

      <div className="flex items-center justify-around py-1.5" style={{ borderTop: "1px solid #E4E6EB" }}>
        {[[ThumbsUp, "Like"], [MessageCircle, "Comment"], [Share2, "Share"]].map(([Icon, label]) => (
          <span key={label} className="flex items-center gap-1.5 text-[12px] font-medium py-1" style={{ color: "#65676B" }}>
            <Icon size={14} /> {label}
          </span>
        ))}
      </div>
    </div>
  );
}

// Google search results placement.
function GooglePreview({ creative, gymName, displayUrl }) {
  return (
    <div className="rounded-[10px] p-4" style={{ background: "#FFFFFF", border: "1px solid #DDE1E7", maxWidth: 520 }}>
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[9px] font-bold" style={{ background: "#D6202B" }}>
          {initials(gymName)}
        </div>
        <div className="min-w-0">
          <p className="text-[12px] leading-tight truncate" style={{ color: "#202124" }}>{gymName}</p>
          <p className="text-[11px] truncate" style={{ color: "#4D5156" }}>{displayUrl}</p>
        </div>
      </div>
      <p className="text-[11px] font-bold mt-2" style={{ color: "#202124" }}>Sponsored</p>
      <p className="text-[18px] leading-snug mt-0.5 hover:underline cursor-pointer" style={{ color: "#1A0DAB" }}>{creative.headline}</p>
      <p className="text-[13px] leading-snug mt-1" style={{ color: "#4D5156" }}>{creative.body}</p>
      {creative.format !== "text" && (
        <p className="text-[11px] mt-2" style={{ color: "#70757A" }}>
          Search ads are text-only — the {creative.format} creative is used on Meta placements.
        </p>
      )}
    </div>
  );
}

export function AdPreview({ creatives = [], platform = "meta", gymName = "Your Gym", defaultPlacement }) {
  const placements = platform === "both" ? ["meta", "google"] : [platform];
  const [placement, setPlacement] = useState(defaultPlacement || placements[0]);
  const [index, setIndex] = useState(0);

  if (!creatives.length) {
    return (
      <div className="rounded-[12px] p-4 text-center" style={{ border: "1px dashed var(--scarlet-hairline)" }}>
        <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>
          Add an ad creative to see how this campaign will look.
        </p>
      </div>
    );
  }

  const creative = creatives[Math.min(index, creatives.length - 1)];
  const displayUrl = hostFrom(creative.display_url || creative.landing_url, "yourgym.com");

  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {placements.length > 1 ? (
          <div className="flex gap-1">
            {placements.map((p) => (
              <button
                key={p} onClick={() => setPlacement(p)}
                className="px-2.5 py-1 rounded-[8px] text-[11px] font-semibold capitalize"
                style={placement === p
                  ? { background: "var(--scarlet-accent)", color: "#fff" }
                  : { background: "var(--scarlet-surface)", color: "var(--scarlet-text-secondary)", border: "1px solid var(--scarlet-hairline)" }}
              >
                {p === "meta" ? "Facebook / Instagram" : "Google Search"}
              </button>
            ))}
          </div>
        ) : (
          <p className="text-[11px] font-semibold" style={{ color: "var(--scarlet-text-secondary)" }}>
            {placement === "meta" ? "Facebook / Instagram feed" : "Google Search"}
          </p>
        )}

        {creatives.length > 1 && (
          <div className="flex gap-1">
            {creatives.map((c, i) => (
              <button
                key={i} onClick={() => setIndex(i)}
                className="px-2 py-1 rounded-[6px] text-[11px] font-semibold"
                style={index === i
                  ? { background: "var(--scarlet-accent)", color: "#fff" }
                  : { background: "var(--scarlet-surface)", color: "var(--scarlet-text-secondary)", border: "1px solid var(--scarlet-hairline)" }}
              >
                {c.variant ? `Variant ${c.variant}` : `Ad ${i + 1}`}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-center p-3 rounded-[12px]" style={{ background: "#EEF0F3" }}>
        {placement === "meta"
          ? <MetaPreview creative={creative} gymName={gymName} displayUrl={displayUrl} />
          : <GooglePreview creative={creative} gymName={gymName} displayUrl={displayUrl} />}
      </div>
      <p className="text-[11px] text-center" style={{ color: "var(--scarlet-text-secondary)" }}>
        Mock-up of how this looks to people on {placement === "meta" ? "Facebook and Instagram" : "Google"}.
        {placement === "meta" && " Like, Comment and Share are part of Facebook's own post layout — not buttons in FitFrik."}
      </p>
    </div>
  );
}
